"use no memo";

/* eslint-disable complexity, import/prefer-default-export */
// Must come before any module that imports Mantine CSS so the @layer order
// is established before Mantine's styles.layer.css declares @layer mantine.
import './styles/tailwind.css';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';

import { MantineProvider } from '@mantine/core';

import {
  captureLandingAttribution,
  isMarketplacePlatform,
  setExperimentId,
} from '@local/utils';
import { mantineTheme } from '@local/styles';
import { ReactQueryClientProvider } from '@local/api';
import { bootstrapGoogleMaps } from '@local/google-maps';

import { ApplicationRoot } from 'ApplicationRoot';

import breakpoints from 'helpers/breakpoints';
import { initMParticle } from '@local/analytics';
import { GOOGLE_MAPS_API_KEYS, PLATFORM_DISPLAY } from 'helpers/constants';
import { getPlatformName } from 'helpers/platform';

import '@mantine/core/styles.layer.css';
import '@mantine/carousel/styles.layer.css';

initMParticle();
captureLandingAttribution();

// todo cn-48139 consolidate all responsive and device logic
const isMobile = (): boolean => {
  return window.innerWidth < breakpoints.xsMax;
};

const hasMapsKey = (
  platform: string
): platform is keyof typeof GOOGLE_MAPS_API_KEYS =>
  platform in GOOGLE_MAPS_API_KEYS;

await (async () => {
  // Child effects fire before parent effects, so installing this in App's
  // effect would be too late for AddressInput's first importLibrary call.
  const platform = String(getPlatformName());
  const apiKey = hasMapsKey(platform)
    ? (GOOGLE_MAPS_API_KEYS[platform] ?? '')
    : '';
  bootstrapGoogleMaps({ apiKey });

  const experimentId = setExperimentId();

  const launchDarklyClientId = isMarketplacePlatform()
    ? process.env.REACT_APP_MARKETPLACE_LAUNCH_DARKLY_CLIENT_ID
    : process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID;

  const LaunchDarklyProvider = await asyncWithLDProvider({
    clientSideID: launchDarklyClientId ?? '',
    context: {
      kind: 'user',
      key: experimentId,
      platform: isMarketplacePlatform()
        ? PLATFORM_DISPLAY.marketplace
        : PLATFORM_DISPLAY.direct,
      deviceType: isMobile() ? 'mobile' : 'desktop',
    },
  });

  createRoot(document.getElementById('root')!).render(
    <LaunchDarklyProvider>
      <ReactQueryClientProvider>
        <MantineProvider theme={mantineTheme}>
          <ApplicationRoot />
        </MantineProvider>
      </ReactQueryClientProvider>
    </LaunchDarklyProvider>
  );
})();
